<template>
  <div class="post__intro">
    <div class="slot">
      <slot />
    </div>
    <div
      ref="menu"
      :class="{ 'collapse-contents': contentsMore }"
    >
      <section class="post__contents">
        <span class="post__contents-title">Содержание</span>
        <TableOfContentsItem
          v-for="item in contentsTokens"
          :key="item.id"
          :item="item"
          :level="0"
        />
      </section>
      <ui-button
        v-if="contentsMore"
        class="view-more-contents-btn"
        data-test="view-more-contents"
        text="Развернуть"
        variant="outline"
        color-mode="neutral"
        size="small"
        @clicked="openModal"
      />
    </div>
    <div
      v-if="isViewShowMoreBlock"
      class="showMore"
      @click="openModal"
    >
      <div class="title">Содержание</div>
      <IconBase
        name="article-icon"
        size="14"
      />
    </div>
    <ui-modal
      :is-shown="isModalShow"
      is-footer-hidden
      size="l"
      title="Содержание"
      @close="closeModal"
    >
      <template #content>
        <section
          class="post__contents-modal"
          @click="closeModal"
        >
          <TableOfContentsItem
            v-for="item in contentsTokens"
            :key="item.id"
            :item="item"
            :level="0"
          />
        </section>
      </template>
    </ui-modal>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onBeforeUnmount, inject } from 'vue';
  import { UiButton } from '@experience/vue3-kit';
  import { UiModal } from '@experience/core-modal-ui';
  import { useScreenSize } from '@/shared/utils/breakpoints';
  import { IconBase } from '@/shared/ui/iconBase';
  import TableOfContentsItem from './TableOfContentsItem.vue';
  import type { ContentToken } from './tableOfContents.types';

  const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);
  const root = shadowRoot || document;

  const props = defineProps<{
    contentsTokens: ContentToken[];
  }>();

  const { isMobile, isTablet } = useScreenSize();

  type ObserverData = {
    observer: IntersectionObserver | null;
    value: boolean | null;
  };

  const menu = ref<HTMLElement | null>(null);
  const isModalShow = ref(false);
  const observerArticles = ref<ObserverData>({
    observer: null,
    value: null,
  });
  const observerMore = ref<ObserverData>({
    observer: null,
    value: null,
  });
  const observerAllMenu = ref<Record<string, ObserverData>>({});
  const linksOnScreen = ref<number | null>(null);

  const countItems = (items: ContentToken[]): number => {
    return items.reduce((count, item) => {
      return count + 1 + (item.children ? countItems(item.children) : 0);
    }, 0);
  };

  const contentsLength = computed(() => countItems(props.contentsTokens || []));
  const mobileAndTabletScreen = computed(() => isMobile.value || isTablet.value);
  const contentsMore = computed(() => contentsLength.value > 10 && mobileAndTabletScreen.value);
  const isViewShowMoreBlock = computed(() => {
    // TODO нужно разобраться почему обсервер оставляет значение 16, после релиза нужно пофиксить
    return observerArticles.value.value && (linksOnScreen.value ?? 16) <= 16 && !observerMore.value.value;
  });

  const openModal = () => {
    isModalShow.value = true;
  };

  const closeModal = () => {
    isModalShow.value = false;
  };

  const handleIntersect = (entries: IntersectionObserverEntry[], obj: ObserverData) => {
    obj.value = entries?.[0]?.intersectionRatio > 0;
  };

  const handleIntersectForList = (entries: IntersectionObserverEntry[], obj: ObserverData) => {
    handleIntersect(entries, obj);
    linksOnScreen.value = Object.values(observerAllMenu.value).filter((el) => el.value)?.length;
  };

  const initObserver = (
    obj: ObserverData,
    node: Element | null,
    callback: (entries: IntersectionObserverEntry[], obj: ObserverData) => void,
  ) => {
    if (!node) {
      return;
    }

    obj.observer = new IntersectionObserver((entries) => {
      callback(entries, obj);
    });

    obj.observer.observe(node);
  };

  const initObserverList = () => {
    const nodes = root.querySelectorAll('.post__contents-item');

    nodes.forEach((el, index) => {
      if (el instanceof HTMLElement) {
        const key = String(index);
        observerAllMenu.value[key] = {
          observer: null,
          value: null,
        };

        initObserver(observerAllMenu.value[key], el, handleIntersectForList);
      }
    });
  };

  onMounted(() => {
    const descriptionNode = root.querySelector('.post-description');
    const moreNode = root.querySelector('.post-more');
    initObserver(observerArticles.value, descriptionNode, handleIntersect);
    initObserver(observerMore.value, moreNode, handleIntersect);

    initObserverList();
  });

  onBeforeUnmount(() => {
    observerArticles.value?.observer?.disconnect();
    observerMore.value?.observer?.disconnect();

    Object.values(observerAllMenu.value).forEach((observerData) => {
      observerData.observer?.disconnect();
    });
  });
</script>

<style lang="scss">
  @import '../../../app/styles/post_page';

  .slot {
    display: flex;
    flex-direction: column;
  }
</style>
